'use client'

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  SxProps,
  Theme,
} from '@mui/material'
import { ReactNode, cloneElement, isValidElement, useState } from 'react'

export type PopConfirmVariant = 'default' | 'danger' | 'warning'

export interface IPopConfirmProps {
  // Nội dung
  title: string
  description?: string | ReactNode

  // Callbacks
  onConfirm: () => void | Promise<void>
  onCancel?: () => void

  // Text buttons
  confirmText?: string
  cancelText?: string

  // Styling
  variant?: PopConfirmVariant
  icon?: ReactNode

  // States
  disabled?: boolean

  // Element trigger
  children: ReactNode

  // Custom styles
  sx?: SxProps<Theme>
}

const variantConfig = {
  default: {
    icon: <InfoOutlinedIcon sx={{ fontSize: 40, color: '#1976d2' }} />,
    confirmColor: '#1976d2',
    confirmHoverColor: '#1565c0',
  },
  danger: {
    icon: <ErrorOutlineIcon sx={{ fontSize: 40, color: '#F04438' }} />,
    confirmColor: '#F04438',
    confirmHoverColor: '#D92D20',
  },
  warning: {
    icon: <WarningAmberIcon sx={{ fontSize: 40, color: '#F79009' }} />,
    confirmColor: '#F79009',
    confirmHoverColor: '#DC6803',
  },
}

export const PopConfirm = ({
  title,
  description,
  onConfirm,
  onCancel,
  confirmText = 'Xác nhận',
  cancelText = 'Hủy',
  variant = 'default',
  icon,
  disabled = false,
  children,
  sx,
}: IPopConfirmProps) => {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const config = variantConfig[variant]
  const displayIcon = icon !== undefined ? icon : config.icon

  // Mở dialog
  const handleOpen = () => {
    if (!disabled) {
      setOpen(true)
    }
  }

  // Đóng dialog
  const handleClose = () => {
    if (!loading) {
      setOpen(false)
      onCancel?.()
    }
  }

  // Xử lý confirm
  const handleConfirm = async () => {
    try {
      setLoading(true)
      const result = onConfirm()

      // Kiểm tra nếu là Promise (async function)
      if (result instanceof Promise) {
        await result
      }

      // Đóng dialog sau khi thành công
      setOpen(false)
    } catch (error) {
      console.error('PopConfirm - Error during confirmation:', error)
      // Không đóng dialog nếu có lỗi, để user có thể thử lại
    } finally {
      setLoading(false)
    }
  }

  // Clone children và thêm onClick handler
  const triggerElement = isValidElement(children)
    ? cloneElement(children as React.ReactElement<any>, {
        onClick: (e: React.MouseEvent) => {
          // Gọi onClick gốc nếu có
          const originalOnClick = (children as React.ReactElement<any>).props.onClick
          originalOnClick?.(e)

          // Mở dialog
          handleOpen()
        },
      })
    : children

  return (
    <>
      {triggerElement}

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="popconfirm-dialog-title"
        aria-describedby="popconfirm-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            minWidth: { xs: '280px', sm: '400px' },
            ...sx,
          },
        }}
      >
        {/* Header với icon */}
        <DialogTitle
          id="popconfirm-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            fontWeight: 600,
            pt: 3,
          }}
        >
          {displayIcon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{displayIcon}</Box>}
          <Box sx={{ flex: 1 }}>{title}</Box>
        </DialogTitle>

        {/* Nội dung mô tả */}
        {description && (
          <DialogContent>
            <DialogContentText id="popconfirm-dialog-description">{description}</DialogContentText>
          </DialogContent>
        )}

        {/* Actions buttons */}
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: '#666',
              textTransform: 'none',
              fontWeight: 500,
              px: 2,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {cancelText}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            loading={loading}
            variant="contained"
            sx={{
              backgroundColor: config.confirmColor,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: config.confirmHoverColor,
              },
              '&.Mui-disabled': {
                backgroundColor: '#BBBBBA',
              },
            }}
          >
            {confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
